import { useCallback, useEffect, useRef } from 'react'

import useAddVariable from '@/common/hooks/useAddVariable/useAddVariable'
import {
  preheaderMaxLength,
  quillSilentSource,
  quillTextChangeEvent,
  quillUserSource,
} from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Preheader/constants'
import {
  getPreheaderText,
  isMentionAutocompletePick,
} from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Preheader/helpers'

const usePreheader = ({ preheader, onChangePreheader }) => {
  const preheaderText = getPreheaderText(preheader)
  const preheaderLength = preheaderText.length

  const handleAddVariableChange = data => {
    if (getPreheaderText(data.html) === preheaderText) return

    onChangePreheader(data)
  }

  const { editorRef, onAddVariable } = useAddVariable({ onChange: handleAddVariableChange })
  const previousChangeRef = useRef(null)

  const handleTextChange = useCallback(
    (delta, oldDelta, source) => {
      const editor = editorRef.current

      if (getPreheaderText(editor.getSemanticHTML()).length <= preheaderMaxLength) {
        previousChangeRef.current = { delta, oldDelta }

        return
      }

      const changeIndex = delta.ops[0].retain || 0
      const isAutocompletePick =
        source === quillUserSource && isMentionAutocompletePick(delta, previousChangeRef.current)

      editor.updateContents(delta.invert(oldDelta), quillSilentSource)

      if (!isAutocompletePick) {
        queueMicrotask(() => editor.setSelection(changeIndex, 0, quillSilentSource))

        return
      }

      const contentsBeforeQuery = previousChangeRef.current.oldDelta
      const queryLength = contentsBeforeQuery.length() - editor.getLength()

      queueMicrotask(() => {
        editor.setContents(contentsBeforeQuery, quillUserSource)
        editor.setSelection(changeIndex + queryLength, 0, quillSilentSource)
      })
    },
    [editorRef]
  )

  useEffect(() => {
    const editor = editorRef.current

    editor.on(quillTextChangeEvent, handleTextChange)

    return () => editor.off(quillTextChangeEvent, handleTextChange)
  }, [editorRef, handleTextChange])

  const handleChangePreheader = data => {
    if (getPreheaderText(data.html).length > preheaderMaxLength) return

    onChangePreheader(data)
  }

  return { editorRef, preheaderLength, handleChangePreheader, onAddVariable }
}

export default usePreheader
